import { useEffect, useState } from "react";
import { useNetworkStatus } from "../../Hooks/useNetworkStatus";

export default function OfflineBanner() {
  const { isOnline, speed } = useNetworkStatus();
  const [wasOffline, setWasOffline] = useState(false);
  const [showRestored, setShowRestored] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowRestored(false);
      setCollapsed(false);
    } else if (wasOffline) {
      setShowRestored(true);
      setCollapsed(false);
      const t = setTimeout(() => {
        setShowRestored(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [isOnline, wasOffline]);

  const visible = !isOnline || showRestored || speed === "slow";
  if (!visible) return null;

  const bgClass = !isOnline
    ? "bg-red-500 text-white"
    : showRestored
    ? "bg-emerald-500 text-white"
    : "bg-amber-400 text-amber-900";

  const dotClass = !isOnline
    ? "bg-red-200"
    : showRestored
    ? "bg-emerald-200"
    : "bg-amber-600 animate-pulse";

  // Collapsed: show only a small pill with an arrow to expand
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        aria-label="Show connection status"
        className={`
          fixed top-0 left-1/2 -translate-x-1/2 z-[9999]
          flex items-center gap-1.5
          px-3 py-1 text-xs font-medium
          rounded-b-lg shadow-md
          transition-all duration-300
          ${bgClass}
        `}
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-3.5 h-3.5"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        fixed top-0 left-0 right-0 z-[9999]
        flex items-center justify-center gap-2
        px-4 py-2 text-sm font-medium
        transition-all duration-300
        ${bgClass}
      `}
    >
      {/* Indicator dot */}
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`}
      />

      {!isOnline && "You are offline. Showing cached content."}
      {isOnline && showRestored && "Connection restored."}
      {isOnline && !showRestored && speed === "slow" && "Slow connection detected. Loading may take longer than usual."}

      {/* Collapse button – always shown except for the auto-dismissing "restored" banner */}
      {!showRestored && (
        <button
          onClick={() => setCollapsed(true)}
          aria-label="Minimize connection status"
          className="ml-2 p-0.5 rounded hover:opacity-80 transition-opacity flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4"
          >
            <path
              fillRule="evenodd"
              d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
